import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Bookmark,
  Briefcase,
  BookOpen,
  Calculator,
  ClipboardList,
  GraduationCap,
  Home,
  Image as ImageIcon,
  LayoutTemplate,
  ListChecks,
  Menu,
  PlayCircle,
  ScanSearch,
  Search,
  Settings,
  StickyNote,
  Store,
  Wand2,
  Wrench,
  X,
} from "lucide-react";

import logoFull from "../assets/branding/logo-full.png";

type NavItem = {
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
};

type NavGroup = {
  title: string;
  items: NavItem[];
};

const groupedNavItems: NavGroup[] = [
  {
    title: "Core Tools",
    items: [
      { path: "/", icon: Home, label: "Home" },
      { path: "/issues", icon: Search, label: "Issues" },
      { path: "/diagnosis", icon: Wrench, label: "Diagnose" },
      { path: "/smart-scan", icon: ScanSearch, label: "Smart Scan" },
      { path: "/cards", icon: ClipboardList, label: "Cards" },
      { path: "/calculator", icon: Calculator, label: "Calculator" },
      { path: "/maintenance", icon: Settings, label: "Maintenance" },
      { path: "/mockup-builder", icon: ImageIcon, label: "Mockup Builder" },
      { path: "/gangsheet-builder", icon: LayoutTemplate, label: "Gangsheet Builder" },
      { path: "/artwork-prep-actions", icon: Wand2, label: "Artwork Prep" },
      { path: "/color-knockout", icon: Wand2, label: "Color Knockout" },
      { path: "/spot-wizard", icon: Wand2, label: "Spot Wizard" },
    ],
  },
  {
    title: "Learning",
    items: [
      { path: "/howto", icon: BookOpen, label: "How-To" },
      { path: "/knowledge-test", icon: GraduationCap, label: "Skill Test" },
      { path: "/glossary", icon: ListChecks, label: "Glossary" },
      { path: "/videos", icon: PlayCircle, label: "Videos" },
    ],
  },
  {
    title: "Business & Resources",
    items: [
      { path: "/vendors", icon: Store, label: "Vendors" },
      { path: "/business-builder", icon: Briefcase, label: "Business Builder" },
      { path: "/bookmarks", icon: Bookmark, label: "Saved" },
      { path: "/notes", icon: StickyNote, label: "Notes" },
    ],
  },
];

function isActivePath(currentPath: string, itemPath: string) {
  if (itemPath === "/") return currentPath === "/";
  return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
}

export function Header() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [menuOpen]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    if (menuOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  return (
    <>
      <header className="sticky top-0 z-50 px-3 pt-3 sm:px-4 sm:pt-4">
        <div className="mx-auto max-w-7xl">
          <div
            className="
              relative overflow-hidden rounded-[22px]
              border border-dtf-accent/20
              bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.16),transparent_42%),linear-gradient(180deg,rgba(5,13,30,0.985)_0%,rgba(4,10,22,0.985)_100%)]
              shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_28px_rgba(37,99,235,0.12)]
              backdrop-blur-xl
            "
          >
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.05),transparent_28%,transparent_72%,rgba(37,99,235,0.05))]" />
            <div className="pointer-events-none absolute inset-0 rounded-[22px] ring-1 ring-inset ring-white/5" />
            <div className="pointer-events-none absolute -top-14 left-1/2 h-28 w-28 -translate-x-1/2 rounded-full bg-dtf-blue/20 blur-3xl" />

            <div className="relative px-3 py-3 md:hidden">
              <div className="flex items-center justify-between gap-3">
                <Link to="/" className="shrink-0">
                  <img
                    src={logoFull}
                    alt="DTF Wizard"
                    className="
                      h-9 w-auto
                      drop-shadow-[0_0_6px_rgba(37,99,235,0.35)]
                      transition-transform duration-200
                      hover:scale-[1.03]
                    "
                  />
                </Link>

                <button
                  type="button"
                  onClick={() => setMenuOpen(true)}
                  aria-label="Open menu"
                  aria-expanded={menuOpen}
                  aria-controls="dtf-header-menu"
                  className="
                    inline-flex shrink-0 items-center gap-2 rounded-2xl
                    border border-white/12
                    bg-[linear-gradient(180deg,rgba(10,20,42,0.96)_0%,rgba(6,13,28,0.96)_100%)]
                    px-3 py-2
                    text-sm font-semibold text-white/90
                    shadow-[0_0_0_1px_rgba(255,255,255,0.03),inset_0_1px_0_rgba(255,255,255,0.04)]
                    transition-all duration-200
                    hover:border-dtf-blue/30 hover:text-white hover:shadow-[0_0_18px_rgba(37,99,235,0.14)]
                  "
                >
                  <Menu className="h-4 w-4" />
                  <span>Menu</span>
                </button>
              </div>

              <div className="mt-2 border-t border-white/8 pt-2 text-center">
                <div className="text-[14px] font-semibold uppercase tracking-[0.10em] text-dtf-blue drop-shadow-[0_0_4px_rgba(37,99,235,0.25)]">
                  Premium DTF Workflow Hub
                </div>
              </div>
            </div>

            <div className="hidden px-5 py-2.5 md:block">
              <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4">
                <Link to="/" className="shrink-0">
                  <img
                    src={logoFull}
                    alt="DTF Wizard"
                    className="
                      h-[34px] w-auto lg:h-[38px]
                      drop-shadow-[0_0_6px_rgba(37,99,235,0.35)]
                      transition-transform duration-200
                      hover:scale-[1.04]
                    "
                  />
                </Link>

                <div className="min-w-0 text-center">
                  <div className="truncate text-[16px] font-semibold uppercase tracking-[0.10em] text-dtf-blue drop-shadow-[0_0_4px_rgba(37,99,235,0.25)]">
                    Premium DTF Workflow Hub
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setMenuOpen(true)}
                    aria-label="Open menu"
                    aria-expanded={menuOpen}
                    aria-controls="dtf-header-menu"
                    className="
                      inline-flex shrink-0 items-center gap-2 rounded-2xl
                      border border-white/12
                      bg-[linear-gradient(180deg,rgba(10,20,42,0.96)_0%,rgba(6,13,28,0.96)_100%)]
                      px-3.5 py-2
                      text-sm font-semibold text-white/90
                      shadow-[0_0_0_1px_rgba(255,255,255,0.03),inset_0_1px_0_rgba(255,255,255,0.04)]
                      transition-all duration-200
                      hover:border-dtf-blue/30 hover:text-white hover:shadow-[0_0_18px_rgba(37,99,235,0.14)]
                    "
                  >
                    <Menu className="h-4 w-4" />
                    <span>Menu</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[60]">
          <button
            type="button"
            aria-label="Close menu overlay"
            className="absolute inset-0 bg-[rgba(2,6,18,0.72)] backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
          />

          <aside
            id="dtf-header-menu"
            className="
              absolute right-0 top-0 h-full w-full max-w-md
              border-l border-white/10
              bg-[linear-gradient(180deg,rgba(8,16,34,0.985)_0%,rgba(4,10,22,0.985)_100%)]
              shadow-[-20px_0_60px_rgba(0,0,0,0.45)]
            "
            aria-label="Main navigation menu"
          >
            <div className="flex h-full flex-col">
              <div className="border-b border-white/10 px-4 py-4 sm:px-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-dtf-blue/90 sm:text-[12px]">
                      Premium DTF Workflow Hub
                    </div>
                    <div className="mt-1 text-lg font-semibold text-white">
                      DTF Wizard Menu
                    </div>
                    <div className="mt-1 text-sm text-white/60">
                      All tools in one clean place
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setMenuOpen(false)}
                    aria-label="Close menu"
                    className="
                      inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl
                      border border-white/10 bg-white/[0.04] text-white/80
                      transition-all duration-200 hover:border-dtf-blue/30 hover:text-white
                    "
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
                <div className="space-y-6">
                  {groupedNavItems.map((group) => (
                    <section key={group.title}>
                      <h2 className="mb-3 text-[11px] font-bold uppercase tracking-[0.12em] text-dtf-blue/80">
                        {group.title}
                      </h2>

                      <div className="grid gap-2.5">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const active = isActivePath(location.pathname, item.path);

                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`
                                group flex items-center gap-3 rounded-2xl border px-3.5 py-3
                                text-sm font-semibold transition-all duration-200
                                ${
                                  active
                                    ? "border-dtf-blue/60 bg-[linear-gradient(180deg,rgba(37,99,235,0.55)_0%,rgba(10,28,64,0.98)_100%)] text-white shadow-[0_0_0_1px_rgba(59,130,246,0.24),0_0_18px_rgba(37,99,235,0.18)]"
                                    : "border-white/10 bg-[linear-gradient(180deg,rgba(13,23,45,0.92)_0%,rgba(7,14,30,0.92)_100%)] text-white/90 hover:border-dtf-blue/30 hover:text-white hover:shadow-[0_0_14px_rgba(37,99,235,0.08)]"
                                }
                              `}
                            >
                              <span
                                className={`
                                  flex h-10 w-10 items-center justify-center rounded-xl border
                                  ${
                                    active
                                      ? "border-dtf-blue/60 bg-dtf-blue/20 text-white"
                                      : "border-white/10 bg-white/[0.03] text-white/80 group-hover:border-dtf-blue/30 group-hover:text-dtf-blue"
                                  }
                                `}
                              >
                                <Icon className="h-5 w-5" />
                              </span>

                              <span className="truncate">{item.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}